import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Calculator, 
  LogOut, 
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  FileText,
  Upload,
  Copy,
  Download,
  FileUp
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminIRS = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [importData, setImportData] = useState("");
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    year: 2024,
    type: "single",
    description: "",
    brackets: []
  });

  const loadTables = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/irs`, { headers: getAuthHeaders() });
      setTables(response.data);
    } catch (error) {
      toast.error("Erro ao carregar tabelas IRS");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const openNewDialog = () => {
    setEditingTable(null);
    setFormData({
      year: 2026,
      type: "single",
      description: "",
      brackets: [{ min_income: 0, max_income: null, rate: 0, deduction: 0 }]
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (table) => {
    setEditingTable(table);
    setFormData({
      year: table.year,
      type: table.type,
      description: table.description,
      brackets: table.brackets
    });
    setIsDialogOpen(true);
  };

  // Duplicar/Copiar tabela existente
  const handleDuplicate = (table) => {
    setEditingTable(null);
    setFormData({
      year: table.year,
      type: table.type,
      description: `${table.description} (Cópia)`,
      brackets: [...table.brackets.map(b => ({ ...b }))]
    });
    setIsDialogOpen(true);
    toast.info("Tabela copiada - edite e guarde como nova");
  };

  const addBracket = () => {
    setFormData({
      ...formData,
      brackets: [...formData.brackets, { min_income: 0, max_income: null, rate: 0, deduction: 0 }]
    });
  };

  // Copiar escalão individual
  const copyBracket = (index) => {
    const bracketToCopy = { ...formData.brackets[index] };
    const newBrackets = [...formData.brackets];
    newBrackets.splice(index + 1, 0, bracketToCopy);
    setFormData({ ...formData, brackets: newBrackets });
    toast.success("Escalão copiado");
  };

  const removeBracket = (index) => {
    setFormData({
      ...formData,
      brackets: formData.brackets.filter((_, i) => i !== index)
    });
  };

  const updateBracket = (index, field, value) => {
    const newBrackets = [...formData.brackets];
    newBrackets[index] = { 
      ...newBrackets[index], 
      [field]: value === "" ? null : parseFloat(value) 
    };
    setFormData({ ...formData, brackets: newBrackets });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTable) {
        await axios.put(`${API}/irs/${editingTable.id}`, formData, { headers: getAuthHeaders() });
        toast.success("Tabela atualizada com sucesso!");
      } else {
        await axios.post(`${API}/irs`, formData, { headers: getAuthHeaders() });
        toast.success("Tabela criada com sucesso!");
      }
      setIsDialogOpen(false);
      loadTables();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao guardar tabela");
    }
  };

  const handleDelete = async (tableId) => {
    if (!window.confirm("Tem a certeza que deseja eliminar esta tabela?")) return;
    try {
      await axios.delete(`${API}/irs/${tableId}`, { headers: getAuthHeaders() });
      toast.success("Tabela eliminada");
      loadTables();
    } catch (error) {
      toast.error("Erro ao eliminar tabela");
    }
  };

  // Exportar tabelas para JSON
  const handleExport = () => {
    const dataToExport = tables.map(({ id, updated_at, ...rest }) => rest);
    const blob = new Blob([JSON.stringify(dataToExport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tabelas_irs_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Tabelas exportadas!");
  };

  // Importar tabelas de ficheiro
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      setImportData(e.target.result);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importData.trim()) {
      toast.error("Cole ou carregue dados JSON para importar");
      return;
    }

    setImporting(true);
    try {
      const parsedData = JSON.parse(importData);
      const tablesToImport = Array.isArray(parsedData) ? parsedData : [parsedData];
      
      let successCount = 0;
      let errorCount = 0;

      for (const table of tablesToImport) {
        try {
          // Validar estrutura básica
          if (!table.year || !table.type || !table.brackets) {
            errorCount++;
            continue;
          }

          await axios.post(`${API}/irs`, {
            year: table.year,
            type: table.type,
            description: table.description || `Tabela IRS ${table.year} - ${table.type}`,
            brackets: table.brackets
          }, { headers: getAuthHeaders() });
          successCount++;
        } catch (err) {
          errorCount++;
        }
      }

      if (successCount > 0) {
        toast.success(`${successCount} tabela(s) importada(s) com sucesso!`);
        loadTables();
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} tabela(s) com erro na importação`);
      }

      setIsImportDialogOpen(false);
      setImportData("");
    } catch (error) {
      toast.error("Erro ao processar JSON. Verifique o formato dos dados.");
    } finally {
      setImporting(false);
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      single: "Não Casado",
      married_1: "Casado (1 Titular)",
      married_2: "Casado (2 Titulares)",
      divorced: "Divorciado(a)",
      widowed: "Viúvo(a)"
    };
    return labels[type] || type;
  };

  // Template de exemplo para importação
  const importTemplate = `[
  {
    "year": 2026,
    "type": "single",
    "description": "Tabela IRS 2026 - Não Casado",
    "brackets": [
      { "min_income": 0, "max_income": 8342, "rate": 12.5, "deduction": 0 },
      { "min_income": 8342, "max_income": 12587, "rate": 15.7, "deduction": 266.94 },
      { "min_income": 12587, "max_income": null, "rate": 21.2, "deduction": 958.72 }
    ]
  }
]`;

  return (
    <div className="min-h-screen bg-background" data-testid="admin-irs-page">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Tabelas IRS</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">{user?.name}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Back & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground">Tabelas IRS</h1>
              <p className="text-muted-foreground">Gerir tabelas de retenção na fonte</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Botão Exportar */}
            {tables.length > 0 && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleExport}
                data-testid="export-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar
              </Button>
            )}

            {/* Botão Importar */}
            <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  size="sm"
                  data-testid="import-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Importar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-xl flex items-center gap-2">
                    <FileUp className="w-5 h-5" />
                    Importar Tabelas IRS
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label>Carregar ficheiro JSON</Label>
                    <div className="flex gap-2">
                      <Input
                        type="file"
                        accept=".json"
                        ref={fileInputRef}
                        onChange={handleFileSelect}
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Ou cole os dados JSON diretamente</Label>
                    <textarea
                      className="w-full h-64 p-3 font-mono text-sm border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder={importTemplate}
                      value={importData}
                      onChange={(e) => setImportData(e.target.value)}
                      data-testid="import-textarea"
                    />
                  </div>

                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800 font-medium mb-1">Formato esperado:</p>
                    <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                      <li><strong>year</strong>: Ano da tabela (ex: 2026)</li>
                      <li><strong>type</strong>: "single", "married_1", "married_2", "divorced" ou "widowed"</li>
                      <li><strong>description</strong>: Descrição da tabela</li>
                      <li><strong>brackets</strong>: Array de escalões com min_income, max_income (null para ilimitado), rate (%), deduction (€)</li>
                    </ul>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button 
                      onClick={handleImport} 
                      disabled={importing || !importData.trim()}
                      className="bg-primary text-primary-foreground"
                      data-testid="confirm-import-btn"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          A importar...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Importar
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Botão Nova Tabela */}
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-brand-600 text-white hover:bg-brand-700 font-bold"
                  onClick={openNewDialog}
                  data-testid="add-table-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Tabela
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-manrope font-bold text-xl">
                    {editingTable ? "Editar Tabela IRS" : "Nova Tabela IRS"}
                  </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ano</Label>
                      <Input
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                        data-testid="year-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tipo</Label>
                      <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                        <SelectTrigger data-testid="type-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single">Não Casado</SelectItem>
                          <SelectItem value="married_1">Casado (1 Titular)</SelectItem>
                          <SelectItem value="married_2">Casado (2 Titulares)</SelectItem>
                          <SelectItem value="divorced">Divorciado(a)</SelectItem>
                          <SelectItem value="widowed">Viúvo(a)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Descrição</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Ex: Tabela IRS 2026 - Não Casado"
                      data-testid="description-input"
                    />
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Escalões</Label>
                      <Button type="button" variant="outline" size="sm" onClick={addBracket}>
                        <Plus className="w-4 h-4 mr-1" /> Adicionar
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {formData.brackets.map((bracket, index) => (
                        <div key={index} className="grid grid-cols-6 gap-2 items-end p-3 bg-secondary rounded-lg">
                          <div>
                            <Label className="text-xs">Mínimo (€)</Label>
                            <Input
                              type="number"
                              value={bracket.min_income || ""}
                              onChange={(e) => updateBracket(index, "min_income", e.target.value)}
                              className="font-mono"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Máximo (€)</Label>
                            <Input
                              type="number"
                              value={bracket.max_income || ""}
                              onChange={(e) => updateBracket(index, "max_income", e.target.value)}
                              placeholder="Ilimitado"
                              className="font-mono"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Taxa (%)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={bracket.rate || ""}
                              onChange={(e) => updateBracket(index, "rate", e.target.value)}
                              className="font-mono"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Dedução (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={bracket.deduction || ""}
                              onChange={(e) => updateBracket(index, "deduction", e.target.value)}
                              className="font-mono"
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => copyBracket(index)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            title="Copiar escalão"
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => removeBracket(index)}
                            className="text-destructive"
                            title="Eliminar escalão"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="bg-primary text-primary-foreground" data-testid="save-btn">
                      {editingTable ? "Guardar Alterações" : "Criar Tabela"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Tables List */}
        <Card className="bg-white border border-border/60 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : tables.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-64 text-center">
                <FileText className="w-12 h-12 text-muted-foreground mb-4" />
                <p className="text-muted-foreground">Nenhuma tabela IRS configurada</p>
                <p className="text-sm text-muted-foreground mb-4">Clique em "Nova Tabela", "Importar" ou carregue os dados iniciais</p>
                <Button variant="outline" onClick={() => setIsImportDialogOpen(true)}>
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Tabelas
                </Button>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="font-semibold">Ano</TableHead>
                    <TableHead className="font-semibold">Tipo</TableHead>
                    <TableHead className="font-semibold">Descrição</TableHead>
                    <TableHead className="font-semibold">Escalões</TableHead>
                    <TableHead className="font-semibold text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tables.map((table) => (
                    <TableRow key={table.id} data-testid={`table-row-${table.id}`}>
                      <TableCell className="font-mono font-medium">{table.year}</TableCell>
                      <TableCell>{getTypeLabel(table.type)}</TableCell>
                      <TableCell className="text-muted-foreground">{table.description}</TableCell>
                      <TableCell className="font-mono">{table.brackets?.length || 0}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDuplicate(table)}
                            title="Duplicar tabela"
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                            data-testid={`duplicate-btn-${table.id}`}
                          >
                            <Copy className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openEditDialog(table)}
                            data-testid={`edit-btn-${table.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDelete(table.id)}
                            data-testid={`delete-btn-${table.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminIRS;
